import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create an axios instance for API calls
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Token storage for setting from React context
let authToken = null;

// Function to set the auth token (called from React context)
export const setAuthToken = (token) => {
  authToken = token;
};

// Function to clear the auth token (called on logout)
export const clearAuthToken = () => {
  authToken = null;
};

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.debug('API request unauthorized');
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth - STRUCT-2: Password change
export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  });
  return response.data;
};

// Auth - STRUCT-1: 2FA endpoints
export const setup2FA = async () => {
  const response = await api.post('/auth/2fa/setup');
  return response.data;
};

export const verify2FA = async (code) => {
  const response = await api.post('/auth/2fa/verify', { code });
  return response.data;
};

export const disable2FA = async (password) => {
  const response = await api.post('/auth/2fa/disable', { password });
  return response.data;
};

// Dashboard
export const fetchDashboard = async () => {
  const response = await api.get('/dashboard');
  return response.data;
};

// Family Members
export const fetchFamilyMembers = async () => {
  const response = await api.get('/family-members');
  return response.data?.members || [];
};

export const createFamilyMember = async (data) => {
  const response = await api.post('/family-members', data);
  return response.data.member;
};

export const updateFamilyMember = async (id, data) => {
  const response = await api.put(`/family-members/${id}`, data);
  return response.data.member;
};

export const deleteFamilyMember = async (id) => {
  await api.delete(`/family-members/${id}`);
};

// Activities
export const fetchActivities = async () => {
  const response = await api.get('/activities');
  return response.data?.activities || [];
};

export const createActivity = async (data) => {
  const response = await api.post('/activities', data);
  return response.data.activity;
};

export const updateActivity = async (id, data) => {
  const response = await api.put(`/activities/${id}`, data);
  return response.data.activity;
};

export const deleteActivity = async (id) => {
  await api.delete(`/activities/${id}`);
};

// Events
export const fetchEvents = async (params = {}) => {
  const response = await api.get('/events', { params });
  return response.data?.events || [];
};

export const createEvent = async (data) => {
  const response = await api.post('/events', data);
  return response.data.event;
};

export const createEventsBulk = async (events) => {
  const response = await api.post('/events/bulk', { events });
  return response.data.events;
};

export const updateEvent = async (id, data) => {
  const response = await api.put(`/events/${id}`, data);
  return response.data.event;
};

export const updateEventStatus = async (id, status) => {
  const response = await api.put(`/events/${id}/status`, { status });
  return response.data.event;
};

export const fetchEventHistory = async () => {
  const response = await api.get('/events/history');
  return response.data.events;
};

export const deleteEvent = async (id) => {
  await api.delete(`/events/${id}`);
};

// Import
export const parseEmail = async (emailContent) => {
  const response = await api.post('/import/email-parse', { email_content: emailContent });
  return response.data.events;
};

export const parseICal = async (icalContent) => {
  const response = await api.post('/import/ical', { ical_content: icalContent });
  return response.data.events;
};

// Summaries
export const getDailySummary = async (date) => {
  const response = await api.post('/summaries/daily', { date });
  return response.data;
};

export const getWeeklySummary = async (date) => {
  const response = await api.post('/summaries/weekly', { date });
  return response.data;
};

// BUG-3: Monthly summary API
export const getMonthlySummary = async (date) => {
  const response = await api.post('/summaries/monthly', { date });
  return response.data;
};

export const getChildSummary = async (childId) => {
  const response = await api.post('/summaries/per-child', { child_id: childId });
  return response.data;
};

// Notifications
export const fetchNotifications = async () => {
  const response = await api.get('/notifications');
  return response.data.notifications;
};

export const markNotificationRead = async (id) => {
  await api.put(`/notifications/${id}/read`);
};

export const markAllNotificationsRead = async () => {
  await api.put('/notifications/read-all');
};

// Calendar Sources
export const fetchCalendarSources = async () => {
  const response = await api.get('/calendar-sources');
  return response.data.sources;
};

export const subscribeCalendar = async (data) => {
  const response = await api.post('/calendar-sources/subscribe', data);
  return response.data;
};

export const uploadICalFile = async (file, name, childId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (name) formData.append('name', name);
  if (childId) formData.append('child_id', childId);
  
  const response = await api.post('/calendar-sources/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const syncCalendarSource = async (sourceId) => {
  const response = await api.post(`/calendar-sources/${sourceId}/sync`);
  return response.data;
};

export const updateCalendarSource = async (sourceId, data) => {
  const response = await api.put(`/calendar-sources/${sourceId}`, data);
  return response.data.source;
};

export const deleteCalendarSource = async (sourceId) => {
  const response = await api.delete(`/calendar-sources/${sourceId}`);
  return response.data;
};

export const getCalendarSourceEvents = async (sourceId) => {
  const response = await api.get(`/calendar-sources/${sourceId}/events`);
  return response.data;
};

// Calendar Embeds
export const fetchCalendarEmbeds = async () => {
  const response = await api.get('/calendar-embeds');
  return response.data.embeds;
};

export const createCalendarEmbed = async (data) => {
  const response = await api.post('/calendar-embeds', data);
  return response.data;
};

export const updateCalendarEmbed = async (embedId, data) => {
  const response = await api.put(`/calendar-embeds/${embedId}`, data);
  return response.data.embed;
};

export const deleteCalendarEmbed = async (embedId) => {
  const response = await api.delete(`/calendar-embeds/${embedId}`);
  return response.data;
};

// AI Chat
export const sendChatMessage = async (message) => {
  const response = await api.post('/chat', { message });
  return response.data;
};

export const getChatSuggestions = async () => {
  const response = await api.get('/chat/suggestions');
  return response.data.suggestions;
};

// Portal Connections
export const fetchPortalTypes = async () => {
  const response = await api.get('/portal-types');
  return response.data.portal_types;
};

export const fetchPortals = async () => {
  const response = await api.get('/portals');
  return response.data.portals;
};

export const createPortalConnection = async (data) => {
  const response = await api.post('/portals', data);
  return response.data;
};

export const syncPortal = async (portalId) => {
  const response = await api.post(`/portals/${portalId}/sync`);
  return response.data;
};

export const reauthenticatePortal = async (portalId, data) => {
  const response = await api.post(`/portals/${portalId}/reauthenticate`, data);
  return response.data;
};

export const updatePortal = async (portalId, data) => {
  const response = await api.put(`/portals/${portalId}`, data);
  return response.data.portal;
};

export const deletePortal = async (portalId) => {
  const response = await api.delete(`/portals/${portalId}`);
  return response.data;
};
